/**
 * Landlord Invoice Mixin
 *
 * "Raise invoice" for a landlord account, shared by landlordSlideover and
 * landlordAccountSlideover. Opens a picker listing the account's unsettled
 * charges (all ticked), then posts the ticked ones to the accounting
 * controller, which snapshots them into a numbered invoice with a PDF and
 * hands back a ready-to-send email draft that we open in the composer with
 * the PDF attached. Nothing on the ledger changes, so no account refresh is
 * needed — only the invoices list.
 */
export function landlordInvoiceMixin() {
  return {
    landlordInvoices: [],

    // Account card sub-tabs — 'transactions' or 'invoices' (the contractor
    // account tab's pattern)
    acctSubTab: 'transactions',

    setAcctSubTab(tab) {
      if (tab === this.acctSubTab) return
      this.clearSearch()
      this.searchOpen = false
      this.acctSubTab = tab
    },

    // Picker state
    invoiceModalOpen: false,
    invoiceLoading: false,
    invoiceCandidates: [],
    invoiceSelected: {},
    raisingInvoice: false,

    // Either slideover's stats object (landlordAccountSlideover keeps `stats`,
    // the landlord slideover's account tab keeps `acctStats`).
    invoiceStats() {
      return this.stats || this.acctStats || null
    },

    unsettledChargesPence() {
      return this.invoiceStats()?.unsettled_charges_pence || 0
    },

    canRaiseInvoice() {
      return this.unsettledChargesPence() > 0
    },

    raiseInvoiceTitle() {
      if (this.canRaiseInvoice()) {
        return `${this.formatPence(this.unsettledChargesPence())} outstanding on this account`
      }
      return 'Nothing outstanding on this account'
    },

    async openInvoiceModal() {
      if (!this.canRaiseInvoice() || !this.landlordId) return
      this.invoiceModalOpen = true
      this.invoiceLoading = true
      this.invoiceCandidates = []
      this.invoiceSelected = {}

      try {
        const response = await fetch(`/accounting/accounts/landlords/${this.landlordId}/invoices/new`, {
          headers: { 'Accept': 'application/json' }
        })
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
        const data = await response.json()
        this.invoiceCandidates = data.charges || []
        const selected = {}
        this.invoiceCandidates.forEach((charge) => { selected[charge.id] = true })
        this.invoiceSelected = selected
      } catch (error) {
        console.error('Failed to load unsettled charges:', error)
        this.invoiceModalOpen = false
        alert('Could not load the unsettled charges. Please try again.')
      } finally {
        this.invoiceLoading = false
      }
    },

    closeInvoiceModal() {
      if (this.raisingInvoice) return
      this.invoiceModalOpen = false
    },

    toggleInvoiceCharge(id) {
      this.invoiceSelected = { ...this.invoiceSelected, [id]: !this.invoiceSelected[id] }
    },

    invoiceAllSelected() {
      return this.invoiceCandidates.length > 0 && this.invoiceCandidates.every((c) => this.invoiceSelected[c.id])
    },

    toggleAllInvoiceCharges() {
      const value = !this.invoiceAllSelected()
      const selected = {}
      this.invoiceCandidates.forEach((charge) => { selected[charge.id] = value })
      this.invoiceSelected = selected
    },

    selectedInvoiceCharges() {
      return this.invoiceCandidates.filter((c) => this.invoiceSelected[c.id])
    },

    invoiceSelectedCount() {
      return this.selectedInvoiceCharges().length
    },

    invoiceSelectedTotal() {
      return this.selectedInvoiceCharges().reduce((sum, c) => sum + (c.gross_pence || 0), 0)
    },

    async raiseInvoice() {
      const chargeIds = this.selectedInvoiceCharges().map((c) => c.id)
      if (chargeIds.length === 0 || this.raisingInvoice || !this.landlordId) return
      this.raisingInvoice = true

      try {
        const response = await fetch(`/accounting/accounts/landlords/${this.landlordId}/invoices`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ charge_ids: chargeIds })
        })
        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          alert(data.error || 'Could not raise the invoice. Please try again.')
          return
        }

        this.landlordInvoices = [data.invoice, ...this.landlordInvoices]
        this.invoiceModalOpen = false
        this.acctSubTab = 'invoices'

        if (data.pdf_error) {
          alert(data.pdf_error)
          return
        }

        const draft = data.draft
        if (!draft?.recipients?.length) {
          alert(`No email address on file for this landlord. ${data.invoice.reference} has been raised — open it from the Invoices list.`)
          return
        }

        window.dispatchEvent(new CustomEvent('open-email-compose', {
          detail: {
            to: draft.recipients,
            subject: draft.subject,
            body_html: draft.body_html,
            attachments: draft.attachment ? [ draft.attachment ] : []
          }
        }))
      } catch (error) {
        console.error('Failed to raise invoice:', error)
        alert('Could not raise the invoice. Please try again.')
      } finally {
        this.raisingInvoice = false
      }
    },

    openInvoicePdf(invoice) {
      if (invoice?.pdf_url) window.open(invoice.pdf_url, '_blank')
    }
  }
}
